import axios from "axios";

// Auto-detect API base: use current origin in production, env var in development
const envUrl = process.env.REACT_APP_BACKEND_URL || "";
const currentOrigin = typeof window !== "undefined" ? window.location.origin : "";
// If the env URL doesn't match the current origin (deployed to custom domain), use current origin
const API_BASE = (envUrl && currentOrigin && !envUrl.includes(window.location.hostname) && window.location.hostname !== "localhost")
  ? currentOrigin
  : envUrl || currentOrigin;

const api = axios.create({
  baseURL: `${API_BASE}/api`,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

// Response interceptor - only retry refresh once, never redirect
let isRefreshing = false;
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    // Skip refresh for auth endpoints themselves
    const isAuthEndpoint = originalRequest?.url?.includes("/auth/");
    if (error.response?.status === 401 && !isAuthEndpoint && !originalRequest._retry && !isRefreshing) {
      originalRequest._retry = true;
      isRefreshing = true;
      try {
        await axios.post(`${API_BASE}/api/auth/refresh`, {}, { withCredentials: true });
        isRefreshing = false;
        return api.request(originalRequest);
      } catch {
        isRefreshing = false;
      }
    }
    return Promise.reject(error);
  }
);

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e))).filter(Boolean).join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}

// Blob downloads lose the backend message, because axios parses the error body as a Blob.
// Read it back as text and pull out the real `detail`.
export async function blobErrorMessage(err, fallback = "Download failed") {
  const data = err?.response?.data;
  if (data instanceof Blob) {
    try {
      const text = await data.text();
      try {
        return formatApiError(JSON.parse(text).detail) || fallback;
      } catch {
        return text.trim() ? text.trim().slice(0, 300) : fallback;
      }
    } catch {
      return fallback;
    }
  }
  if (data?.detail != null) return formatApiError(data.detail);
  return fallback;
}

export default api;
